import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Filter,
  Star,
  TrendingUp,
  BarChart2,
  MessageSquare,
} from 'lucide-react';
import { useDmdilly } from './dmdilly-context';
import { getInbox, updateSubmissionStatus } from './api';
import SubmissionCard from './ui/submission-card';
import LoadingSpinner from './ui/loading-spinner';
import ErrorMessage from './ui/error-message';

const STATUS_TABS = [
  { value: null, label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'filtered', label: 'Filtered' },
];

export default function PriorityInbox({ creatorIdOverride }) {
  const { apiBaseUrl, creatorId: contextCreatorId } = useDmdilly();
  const creatorId = creatorIdOverride || contextCreatorId;

  const [submissions, setSubmissions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const selectedSubmission = submissions.find((s) => s.id === selectedId);

  useEffect(() => {
    fetchInbox();
  }, [creatorId, statusFilter]);

  const fetchInbox = async () => {
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await getInbox(
      apiBaseUrl,
      creatorId,
      statusFilter
    );

    if (apiError) {
      setError(apiError);
      setLoading(false);
      return;
    }

    // Map and flatten submissions to expected shape
    const mapped = ((data && data.data && data.data.submissions) ? data.data.submissions : []).map((s) => {
      // Combine questions and answers into Q&A array
      let questions_and_answers = [];
      if (Array.isArray(s.questions) && Array.isArray(s.answers)) {
        questions_and_answers = s.questions.map((q, i) => ({
          question: q,
          answer: s.answers[i] || '',
        }));
      }
      return {
        id: s._id,
        creatorId: s.creatorId,
        fanId: s.fanId,
        status: s.status,
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
        // Fan info
        fan_name: s.fanProfile?.fan_name || undefined,
        fan_summary: s.fanProfile?.summary || undefined,
        // Scores
        fit_score: s.scoring?.fit_score ?? null,
        priority_score: s.scoring?.priority_score ?? null,
        quality_score: s.scoring?.quality_score ?? null,
        // Scoring reasons
        scoring_reasons: Array.isArray(s.scoring?.reasons)
          ? s.scoring.reasons.join('\n')
          : undefined,
        recommended_action: s.scoring?.recommended_action || undefined,
        // Q&A
        questions_and_answers,
        // Spread all other original fields for fallback
        ...s,
      };
    });

    const sorted = mapped.sort(
      (a, b) => (b.priority_score || 0) - (a.priority_score || 0)
    );
    setSubmissions(sorted);

    if (sorted.length > 0 && !selectedId) {
      setSelectedId(sorted[0].id);
    }

    setLoading(false);
  };

  const handleStatusUpdate = async (submissionId, newStatus) => {
    setActionLoading(true);

    const { error: apiError } = await updateSubmissionStatus(
      apiBaseUrl,
      submissionId,
      newStatus
    );

    if (apiError) {
      setError(apiError);
      setActionLoading(false);
      return;
    }

    setSubmissions((prev) =>
      prev.map((s) => (s.id === submissionId ? { ...s, status: newStatus } : s))
    );
    setActionLoading(false);
  };

  if (loading && submissions.length === 0) {
    return <LoadingSpinner message="Loading inbox..." />;
  }

  if (error && submissions.length === 0) {
    return <ErrorMessage message={error} onRetry={fetchInbox} />;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h2 className="text-xl font-bold text-gray-800">Priority Inbox</h2>
        <button
          onClick={fetchInbox}
          disabled={loading}
          className="p-2 text-gray-500 hover:text-boggler-teal hover:bg-boggler-teal/10 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 p-4 border-b border-gray-100 overflow-x-auto">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.label}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === tab.value
                ? 'bg-boggler-teal text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {submissions.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <MessageSquare size={48} className="text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">
            No submissions yet
          </h3>
          <p className="text-gray-500">
            Share your fan page link to start receiving prioritized messages.
          </p>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* List */}
          <div className="border-r border-gray-100 overflow-y-auto p-4 space-y-3">
            {submissions.map((submission) => (
              <>
                <SubmissionCard
                  key={submission.id}
                  submission={submission}
                  selected={submission.id === selectedId}
                  onClick={() => setSelectedId(submission.id)}
                />

                {/* Detail */}
                <div className="flex-1 overflow-y-auto p-6">
                  {selectedSubmission ? (
                    <motion.div
                      key={selectedSubmission.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-6"
                    >
                      {/* Fan Info */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                          {selectedSubmission.fan_name || 'Anonymous'}
                        </h3>
                        <p className="text-gray-600">
                          {selectedSubmission.fan_summary || 'No summary available'}
                        </p>
                      </div>

                      {/* Scores */}
                      <div className="flex flex-wrap gap-4">
                        <div className="bg-yellow-50 rounded-xl p-4 text-center">
                          <Star className="text-yellow-500 mx-auto mb-2" size={24} />
                          <p className="text-2xl font-bold text-gray-800">
                            {selectedSubmission.fit_score?.toFixed(1) || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">Fit Score</p>
                        </div>
                        <div className="bg-boggler-teal/10 rounded-xl p-4 text-center">
                          <TrendingUp
                            className="text-boggler-teal mx-auto mb-2"
                            size={24}
                          />
                          <p className="text-2xl font-bold text-gray-800">
                            {selectedSubmission.priority_score?.toFixed(1) || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">Priority Score</p>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-4 text-center">
                          <BarChart2
                            className="text-blue-500 mx-auto mb-2"
                            size={24}
                          />
                          <p className="text-2xl font-bold text-gray-800">
                            {selectedSubmission.quality_score?.toFixed(1) || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">Quality Score</p>
                        </div>
                      </div>

                      {/* Analysis */}
                      {selectedSubmission.scoring_reasons && (
                        <div className="bg-gray-50 rounded-xl p-4">
                          <h4 className="font-medium text-gray-800 mb-2">Analysis</h4>
                          <p className="text-sm text-gray-600">
                            {selectedSubmission.scoring_reasons}
                          </p>
                        </div>
                      )}

                      {/* Recommended Action */}
                      {selectedSubmission.recommended_action && (
                        <div className="bg-boggler-teal/10 rounded-xl p-4">
                          <h4 className="font-medium text-boggler-teal mb-1">
                            Recommended Action
                          </h4>
                          <p className="text-sm text-boggler-teal">
                            {selectedSubmission.recommended_action}
                          </p>
                        </div>
                      )}

                      {/* Original Q&A */}
                      {selectedSubmission.questions_and_answers &&
                        selectedSubmission.questions_and_answers.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-800 mb-3">
                              Original Responses
                            </h4>
                            <div className="space-y-3">
                              {selectedSubmission.questions_and_answers.map(
                                (qa, index) => (
                                  <div
                                    key={index}
                                    className="bg-white border border-gray-100 rounded-xl p-4"
                                  >
                                    <p className="text-sm font-medium text-gray-700 mb-2">
                                      Q: {qa.question}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      A: {qa.answer}
                                    </p>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}

                      {/* Actions */}
                      {selectedSubmission.status === 'pending' && (
                        <div className="flex gap-3 pt-4">
                          <button
                            onClick={() =>
                              handleStatusUpdate(selectedSubmission.id, 'accepted')
                            }
                            disabled={actionLoading}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            <CheckCircle size={18} />
                            Accept
                          </button>
                          <button
                            onClick={() =>
                              handleStatusUpdate(selectedSubmission.id, 'declined')
                            }
                            disabled={actionLoading}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            <XCircle size={18} />
                            Decline
                          </button>
                          <button
                            onClick={() =>
                              handleStatusUpdate(selectedSubmission.id, 'filtered')
                            }
                            disabled={actionLoading}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors disabled:opacity-50"
                          >
                            <Filter size={18} />
                            Filter
                          </button>
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      Select a submission to view details
                    </div>
                  )}
                </div>
              </>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
